"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Menu = void 0;
var _helper_1 = require("./_helper");
var instanceOfResponsiveConfig = function (object) {
    return 'minWidth' in object;
};
/**
 * A11Y Menu. A fully accessible menu.
 * @author Wanja Friedemann Pflüger <noreply@wanjapflueger.de>
 * @example
 *   import { Menu } from "@wanjapflueger/a11y-menu";
 *   const myMenu = new Menu();
 */
var Menu = /** @class */ (function () {
    function Menu() {
        /** All focussable elements within nav (will be `HTMLLIElement`s that have submenus and `HTMLAnchorElement`s) */
        this.focussableElements = [];
        /** All {@link MenuItem}s in {@link theMenu} */
        this.menuItems = [];
    }
    Object.defineProperty(Menu.prototype, "elements", {
        /**
         * The menu elements
         * @example
         *   myMenu.elements
         *   myMenu.elements.foo
         *   ...
         */
        get: function () {
            var _a, _b, _c;
            /** The first `<ul>` ELement in {@link theMenu} */
            var list = (_a = this.theMenu) === null || _a === void 0 ? void 0 : _a.querySelector(':scope > ul');
            /** All `HTMLUListElement`s that are not {@link list} */
            var uListElements = (_b = this.theMenu) === null || _b === void 0 ? void 0 : _b.querySelectorAll(':scope > ul ul');
            /** All `HTMLSpanElement`s that are the caption for a submenu */
            var spanElements = this.theMenu
                ? Array.from(this.theMenu.querySelectorAll('span')).filter(function (el) {
                    var _a, _b;
                    return ((_a = el.parentElement) === null || _a === void 0 ? void 0 : _a.nodeName) === 'LI' &&
                        ((_b = el.parentElement) === null || _b === void 0 ? void 0 : _b.querySelector(':scope > ul'));
                })
                : undefined;
            /** All `HTMLLIElement`s and `HTMLAnchorElement` in {@link list} */
            var listItems = (_c = this.theMenu) === null || _c === void 0 ? void 0 : _c.querySelectorAll('li, a');
            return {
                list: list,
                uListElements: uListElements,
                spanElements: spanElements,
                listItems: listItems,
            };
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Menu.prototype, "config", {
        /**
         * The menu configuration
         * @example
         *   myMenu.config
         *   myMenu.config.config
         *   ...
         */
        get: function () {
            var _a, _b;
            return {
                responsive: (_b = (_a = this.parameters) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.responsive,
            };
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Create menu
     * @example
     *   ```html
     *   <nav id="my-nav">
     *     <ul>
     *       <li><a href="/">Home</a></li>
     *       <li>
     *         <span>Topics</span>
     *         <ul>
     *           <li><a href="/nature">Nature</a></li>
     *           <li><a href="/people">People</a></li>
     *         </ul>
     *       </li>
     *       <li><a href="/contact">Contact</a></li>
     *     </ul>
     *   </nav>
     *   <script>
     *     import { Menu } from "@wanjapflueger/a11y-menu";
     *     const myMenu = new Menu();
     *     myMenu.Create({
     *       nav: document.getElementById('my-nav')
     *     })
     *   </script>
     *   ```
     * @returns True if successfull
     */
    Menu.prototype.Create = function (parameters) {
        var _this = this;
        var _a, _b, _c, _d;
        /** The default value for {@link Directions.direction} */
        var defaultDirection = 'y';
        /** The default value for {@link Config} */
        var defaultConfig = {
            responsive: [
                {
                    minWidth: 1280,
                    directions: [
                        {
                            level: 0,
                            direction: 'x',
                        },
                    ],
                },
            ],
        };
        // apply parameters
        if (!parameters)
            return false;
        this.parameters = parameters;
        if (!this.parameters.config) {
            // set default Config
            this.parameters.config = defaultConfig;
        }
        // make sure parameters.nav is a <nav> element
        if (!this.parameters.nav || this.parameters.nav.nodeName !== 'NAV')
            return false;
        // make sure config is valid
        if (((_b = (_a = this.parameters.config) === null || _a === void 0 ? void 0 : _a.responsive) === null || _b === void 0 ? void 0 : _b.length) &&
            !instanceOfResponsiveConfig(this.parameters.config.responsive[0]))
            return false;
        // make sure no ResponsiveConfig.minWidth value is < 0
        if (((_d = (_c = this.parameters.config) === null || _c === void 0 ? void 0 : _c.responsive) === null || _d === void 0 ? void 0 : _d.length) &&
            this.parameters.config.responsive.find(function (item) { return item.minWidth < 0; }) !==
                undefined)
            return false;
        this.theMenu = this.parameters.nav;
        /**
         * Reset all {@link MenuItem}s in a {@link MenuItem.level}
         */
        var resetAllInLevel = function (level) {
            _this.menuItems.forEach(function (menuItem) {
                if (level) {
                    if (menuItem.level === level) {
                        if (menuItem.el.nodeName === 'LI') {
                            menuItem.el.setAttribute('aria-expanded', 'false');
                        }
                    }
                }
                else {
                    if (menuItem.el.nodeName === 'LI') {
                        menuItem.el.setAttribute('aria-expanded', 'false');
                    }
                }
            });
        };
        /**
         * Set default `role` attribute values on all `HTMLElement`s that belong to {@link theMenu} (like `role="menubar"`, `role="menuitem"`, etc.)
         */
        var setDefaultAttributes = function () {
            var _a, _b, _c, _d;
            /** set `role` to `menubar` on the first `HTMLUListElement` */
            (_a = _this.elements.list) === null || _a === void 0 ? void 0 : _a.setAttribute('role', 'menubar');
            /** set `role` to `menu` on all `HTMLUListElement`s */
            (_b = _this.elements.uListElements) === null || _b === void 0 ? void 0 : _b.forEach(function (item) {
                item.setAttribute('role', 'menu');
            });
            /** set `role` to `none` on all `HTMLLIElement`s */
            (_c = _this.elements.listItems) === null || _c === void 0 ? void 0 : _c.forEach(function (item) {
                var _a;
                item.setAttribute('role', 'none');
                /* find elements that are focussable (`HTMLLIElement`s with submenu or `HTMLAnchorElement`s) */
                if (item.querySelector(':scope > ul') || item.nodeName === 'A') {
                    (_a = _this.focussableElements) === null || _a === void 0 ? void 0 : _a.push(item);
                }
            });
            /** use `HTMLSpanElement`s as label for `HTMLUListElement`s – given the are a submenu item */
            (_d = _this.elements.spanElements) === null || _d === void 0 ? void 0 : _d.forEach(function (item) {
                var _a;
                var id = _helper_1.makeId(10, undefined, 'label-');
                item.id = id;
                var submenuItem = (_a = item.parentElement) === null || _a === void 0 ? void 0 : _a.querySelector(':scope > ul');
                submenuItem === null || submenuItem === void 0 ? void 0 : submenuItem.setAttribute('aria-labelledby', id);
            });
        };
        var findMenuItemParameters = function () {
            /**
             * Find out the {@link MenuItem.level} of a {@link MenuItem}
             * @returns {number}
             */
            var findLevel = function (menuItem) {
                var _a;
                /** 1 = Level 1, 2 = Level 2, ... */
                var level = 0;
                var match = menuItem.el;
                while (match) {
                    match =
                        match && match.nodeName === 'A'
                            ? (_a = match.parentNode) === null || _a === void 0 ? void 0 : _a.parentNode
                            : match.parentNode;
                    if (match === _this.elements.list)
                        break;
                    if ((match === null || match === void 0 ? void 0 : match.nodeName) === 'LI') {
                        level++;
                    }
                }
                return level;
            };
            /**
             * Find out if {@link MenuItem} is the first in its submenu
             */
            var checkIfIsFirst = function (menuItem) {
                return _this.menuItems &&
                    _this.menuItems.length &&
                    _this.menuItems[menuItem.index - 1] &&
                    _this.menuItems[menuItem.index - 1].level === menuItem.level - 1
                    ? true
                    : false;
            };
            /**
             * Find out if {@link MenuItem} is the last in its submenu
             */
            var checkIfIsLast = function (menuItem) {
                return _this.menuItems &&
                    !_this.menuItems.filter(function (el) {
                        return el.level === menuItem.level && el.index === menuItem.index + 1;
                    }).length
                    ? true
                    : false;
            };
            /**
             * Find out if a {@link MenuItem} has a submenu and find the first {@link MenuItem} in that submenu
             */
            var findSubMenuItem = function (menuItem) {
                var _a;
                if (!menuItem.el.querySelectorAll(':scope > ul').length &&
                    !((_a = menuItem.el.parentNode) === null || _a === void 0 ? void 0 : _a.querySelectorAll(':scope > ul').length))
                    return;
                var match;
                var items = _this.menuItems.filter(function (el) { return el.index > menuItem.index && el.level === menuItem.level + 1; });
                if (items && items.length) {
                    match = items[0];
                }
                return match;
            };
            /**
             * Find out if a {@link MenuItem} is within a submenu and therefore has a parent menu and find the belonging {@link MenuItem}
             */
            var findParentMenuItem = function (menuItem) {
                var match;
                var items = _this.menuItems.filter(function (el) { return el.index < menuItem.index; });
                if (!items || !items.length)
                    return;
                items.reverse();
                match = items.find(function (el) { return el.level === menuItem.level - 1; });
                return match;
            };
            /**
             * Find the next {@link MenuItem} that would follow the current {@link MenuItem}
             */
            var findNextMenuItem = function (menuItem) {
                var match;
                match = _this.menuItems.find(function (el) { return el.level === menuItem.level && el.index > menuItem.index; });
                if (!match) {
                    var sameLevelItems = _this.menuItems.filter(function (el) { return el.level === menuItem.level; });
                    if (sameLevelItems && sameLevelItems.length) {
                        match = sameLevelItems[0];
                    }
                }
                return match;
            };
            /**
             * Find the previous {@link MenuItem} that would precede the current {@link MenuItem}
             */
            var findPreviousMenuItem = function (menuItem) {
                var match;
                var menuItemsReversed = _this.menuItems.slice().reverse();
                if (!menuItemsReversed || !menuItemsReversed.length)
                    return;
                menuItemsReversed.find(function (el) {
                    if (!match) {
                        if (el.level === menuItem.level && el.index < menuItem.index) {
                            match = el;
                        }
                    }
                });
                if (!match) {
                    var sameLevelItems = _this.menuItems.filter(function (el) { return el.level === menuItem.level; });
                    if (sameLevelItems && sameLevelItems.length) {
                        match = sameLevelItems[sameLevelItems.length - 1];
                    }
                }
                return match;
            };
            /**
             * Find values for each {@link MenuItem}
             */
            var findMenuItemElements = function () {
                var _loop_1 = function (i, n) {
                    var item = n[i];
                    /** Define {@link MenuItem} for the first time */
                    var menuItem = {
                        el: item.nodeName === 'A'
                            ? item
                            : item,
                        index: -1,
                        level: -1,
                        isFirst: false,
                        isLast: false,
                        current: false,
                        menu: _this.theMenu,
                        goToPreviousItem: function () {
                            if (!menuItem.current)
                                return false;
                            if (menuItem.level === 0)
                                resetAllInLevel();
                            if (menuItem.previous) {
                                menuItem.previous.el.focus();
                                return true;
                            }
                            return false;
                        },
                        goToNextItem: function () {
                            if (!menuItem.current)
                                return false;
                            if (menuItem.level === 0)
                                resetAllInLevel();
                            if (menuItem.next) {
                                menuItem.next.el.focus();
                                return true;
                            }
                            return false;
                        },
                        goToPreviousLevel: function () {
                            if (!menuItem.current)
                                return false;
                            if (menuItem.parentMenuItem) {
                                if (menuItem.el.getAttribute('aria-expanded') === 'true') {
                                    menuItem.el.setAttribute('aria-expanded', 'false');
                                }
                                menuItem.parentMenuItem.el.focus();
                                return true;
                            }
                            return false;
                        },
                        goToNextLevel: function () {
                            if (!menuItem.current)
                                return false;
                            if (menuItem.subMenuItem) {
                                menuItem.el.setAttribute('aria-expanded', 'true');
                                menuItem.subMenuItem.el.focus();
                                return true;
                            }
                            return false;
                        },
                        goToNextRootItem: function () {
                            var _a;
                            if (!menuItem.current)
                                return false;
                            var currenLevel1Item = _this.menuItems.find(function (el) {
                                return el.level === 0 &&
                                    el.el.getAttribute('aria-expanded') === 'true';
                            });
                            if (currenLevel1Item) {
                                resetAllInLevel();
                                (_a = currenLevel1Item.next) === null || _a === void 0 ? void 0 : _a.el.focus();
                                return true;
                            }
                            return false;
                        },
                    };
                    _this.menuItems.push(menuItem);
                };
                for (var i = 0, n = _this.focussableElements; i < n.length; i++) {
                    _loop_1(i, n);
                }
            };
            findMenuItemElements();
            if (!_this.menuItems)
                return;
            _this.menuItems.forEach(function (menuItem, i) {
                menuItem.index = i;
                menuItem.level = findLevel(menuItem);
                menuItem.current = false;
            });
            _this.menuItems.forEach(function (menuItem, i) {
                menuItem.isFirst = checkIfIsFirst(menuItem);
                menuItem.isLast = checkIfIsLast(menuItem);
                menuItem.next = findNextMenuItem(menuItem);
                menuItem.previous = findPreviousMenuItem(menuItem);
                menuItem.parentMenuItem = findParentMenuItem(menuItem);
                menuItem.subMenuItem = findSubMenuItem(menuItem);
            });
        };
        /**
         * Bind event listeners
         */
        var bindEventListeners = function (menuItem) {
            /**
             * Click handler for a {@link MenuItem}
             */
            var onClick = function (e) {
                if (!e)
                    return;
                e.stopPropagation(); // prevent events fired by parent elements that also listen on click
                var target = e.target;
                /** Similar to {@link MenuItem.el} */
                var possibleMenuItemEl;
                /** make sure we hit an element, that is related to a {@link MenuItem} */
                while (!possibleMenuItemEl && target && target.parentElement) {
                    if (target.getAttribute('role') !== 'menuitem') {
                        target = target.parentElement;
                    }
                    else {
                        possibleMenuItemEl = target;
                    }
                }
                // no match found
                if (!target)
                    return;
                /** possible {@link MenuItem} is not a `HTMLLIElement` (only `HTMLLIElement`s should trigger {@link MenuItem.goToNextLevel} or {@link MenuItem.goToNextItem}) */
                if (!possibleMenuItemEl || possibleMenuItemEl.nodeName !== 'LI')
                    return;
                /** Find the {@link MenuItem}, that belongs to the clicked target */
                var theMenuItem = _this.menuItems.find(function (el) { return el.el === possibleMenuItemEl; });
                if (!theMenuItem)
                    return;
                if (theMenuItem.subMenuItem) {
                    if (theMenuItem.el.getAttribute('aria-expanded') !== 'true') {
                        /** Open {@link MenuItem}, close all already opened submenus and and go to next level */
                        resetAllInLevel(theMenuItem.level);
                        theMenuItem.goToNextLevel();
                    }
                    else {
                        /** Close {@link MenuItem} */
                        theMenuItem.el.setAttribute('aria-expanded', 'false');
                    }
                }
            };
            /**
             * Controls for {@link MenuItem}
             */
            var menuItemControls = function (e) {
                var _a, _b, _c;
                if (!e)
                    return;
                var theDirections;
                if (!_this.config.responsive)
                    return false;
                _this.config.responsive.forEach(function (responsiveConfig) {
                    if (responsiveConfig.minWidth < document.body.clientWidth) {
                        theDirections = responsiveConfig.directions;
                    }
                });
                var direction = ((_a = theDirections === null || theDirections === void 0 ? void 0 : theDirections.find(function (el) { return el.level === menuItem.level; })) === null || _a === void 0 ? void 0 : _a.direction) ||
                    defaultDirection;
                // maintain the functionality of shortcuts and key-combinations by ignoring ctrl, shift, meta (Win/cmd) and alt keys
                if (e.ctrlKey || e.shiftKey || e.metaKey || e.altKey)
                    return;
                if (!menuItem.current)
                    return;
                switch (e.code) {
                    case 'ArrowLeft':
                        e.preventDefault();
                        if (direction === 'x') {
                            menuItem.goToPreviousItem();
                        }
                        else {
                            if (menuItem.level === 1) {
                                menuItem.goToPreviousLevel();
                                (_b = menuItem.parentMenuItem) === null || _b === void 0 ? void 0 : _b.goToPreviousItem();
                            }
                            else {
                                resetAllInLevel(menuItem.level - 1);
                                menuItem.goToPreviousLevel();
                            }
                        }
                        break;
                    case 'ArrowRight':
                        e.preventDefault();
                        if (direction === 'x') {
                            menuItem.goToNextItem();
                        }
                        else {
                            if (!menuItem.goToNextLevel()) {
                                if (menuItem.level === 1) {
                                    menuItem.goToPreviousLevel();
                                    (_c = menuItem.parentMenuItem) === null || _c === void 0 ? void 0 : _c.goToNextItem();
                                }
                                else {
                                    menuItem.goToNextRootItem();
                                }
                            }
                        }
                        break;
                    case 'ArrowUp':
                        e.preventDefault();
                        if (direction === 'y') {
                            if (!menuItem.isFirst) {
                                menuItem.goToPreviousItem();
                            }
                        }
                        else {
                            menuItem.goToPreviousLevel();
                        }
                        break;
                    case 'ArrowDown':
                        e.preventDefault();
                        if (direction === 'y') {
                            menuItem.goToNextItem();
                        }
                        else {
                            menuItem.goToNextLevel();
                        }
                        break;
                    case 'Space':
                    case 'Enter':
                        if (menuItem.el.nodeName === 'LI') {
                            menuItem.goToNextLevel();
                        }
                        break;
                    case 'Escape':
                        e.preventDefault();
                        resetAllInLevel(menuItem.level - 1);
                        menuItem.goToPreviousLevel();
                        break;
                    default:
                        break;
                }
            };
            if (menuItem.el.nodeName === 'LI') {
                menuItem.el.addEventListener('click', onClick);
            }
            var onAnchorLinkClick = function () {
                resetAllInLevel();
            };
            if (menuItem.el.nodeName === 'A') {
                menuItem.el.addEventListener('click', onAnchorLinkClick);
            }
            /**
             * Check the status of any {@link MenuItem} and apply attributes and styles
             */
            var updateMenuItem = function (el) {
                /** Check if {@link MenuItem} is the current {@link MenuItem} */
                if (el.current) {
                    el.el.dataset.current = 'true';
                    document.addEventListener('keydown', menuItemControls);
                }
                else {
                    el.el.dataset.current = 'false';
                    document.removeEventListener('keydown', menuItemControls);
                }
            };
            var onMenuItemFocus = function () {
                var _a;
                (_a = _this.menuItems) === null || _a === void 0 ? void 0 : _a.filter(function (el) { return el.current === true; }).map(function (el) {
                    el.current = false;
                    updateMenuItem(el);
                });
                menuItem.current = true;
                updateMenuItem(menuItem);
            };
            menuItem.el.addEventListener('focus', onMenuItemFocus);
            menuItem.el.addEventListener('focusout', function () {
                document.removeEventListener('keydown', menuItemControls);
            });
        };
        /**
         * Set attributes on each {@link MenuItem} (requires each {@link MenuItem} to be complete)
         */
        var setRelativeAttributes = function () {
            /** Now each {@link MenuItem} is complete and we can apply styles etc. relative to the {@link MenuItem}s data */
            _this.menuItems.forEach(function (menuItem, i) {
                menuItem.el.tabIndex = i === 0 ? 0 : -1;
                menuItem.el.dataset.level = menuItem.level.toString();
                menuItem.el.setAttribute('role', 'menuitem');
                if (menuItem.subMenuItem) {
                    menuItem.el.setAttribute('aria-expanded', 'false');
                }
                bindEventListeners(menuItem);
            });
        };
        setDefaultAttributes();
        findMenuItemParameters();
        setRelativeAttributes();
        _helper_1.onClickOutside(this.theMenu, resetAllInLevel);
        return true;
    };
    return Menu;
}());
exports.Menu = Menu;
